import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import DoctorLayout from "@/pages/doctor/DoctorLayout";
import KpiCard from "@/components/KpiCard";
import AiSummaryDigestCard from "@/components/AiSummaryDigestCard";
import api from "@/lib/api";
import { formatDate } from "@/lib/date";
import { Button } from "@/components/ui/button";
import { TypeBadge } from "@/components/StatusBadge";
import { useAuth } from "@/context/AuthContext";
import {
  Users,
  Clock,
  UserCheck,
  UserPlus,
  CalendarClock,
  AlertTriangle,
  ShieldAlert,
  FlaskConical,
  User,
  ArrowRight,
} from "lucide-react";

const RISK_LABEL = (level) => level.charAt(0) + level.slice(1).toLowerCase();

export default function DoctorDashboard() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  const load = useCallback(() => api.get("/doctor/dashboard").then(({ data }) => setData(data)).catch(() => {}), []);

  useEffect(() => {
    load();
    const interval = setInterval(load, 15000);
    return () => clearInterval(interval);
  }, [load]);

  const sub = data?.subscription;

  return (
    <DoctorLayout>
      {sub?.exceeded && (
        <div
          data-testid="subscription-limit-banner"
          className="mb-6 flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-800"
        >
          <AlertTriangle className="h-4 w-4 shrink-0" />
          You have exceeded your monthly appointment limit. Future appointments will be restricted in 24 hours. Please upgrade your plan.
        </div>
      )}

      <h1 className="text-2xl font-semibold text-slate-900" data-testid="doctor-dashboard-heading">
        Good day, {user?.name || "Doctor"}
      </h1>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <KpiCard testId="kpi-todays-appointments" label="Today's Appointments" value={data?.todays_appointments ?? "—"} icon={Clock} onClick={() => navigate("/doctor/queue")} />
            <KpiCard testId="kpi-waiting-patients" label="Waiting Patients" value={data?.waiting_patients ?? "—"} icon={UserCheck} onClick={() => navigate("/doctor/queue?status=WAITING")} />
            <KpiCard testId="kpi-total-patients" label="Total Patients" value={data?.total_patients ?? "—"} icon={Users} onClick={() => navigate("/doctor/patients")} />
            <KpiCard testId="kpi-new-patients-month" label="New Patients This Month" value={data?.new_patients_month ?? "—"} icon={UserPlus} onClick={() => navigate("/doctor/patients?risk_filter=new_this_month")} />
            <KpiCard testId="kpi-followups-due" label="Follow-ups Due" value={data?.followups_due ?? "—"} icon={CalendarClock} onClick={() => navigate("/doctor/queue?status=FOLLOW_UP_SCHEDULED&scope=all")} />
            <KpiCard testId="kpi-missed-followups" label="Missed Follow-ups" value={data?.missed_followups ?? 0} icon={AlertTriangle} accent="text-amber-500" onClick={() => navigate("/doctor/patients?risk_filter=missed_followups")} />
            <KpiCard testId="kpi-high-risk" label="High-Risk Patients" value={data?.high_risk_patients ?? 0} icon={ShieldAlert} accent="text-red-600" onClick={() => navigate("/doctor/patients?risk_filter=high_risk")} />
            <KpiCard testId="kpi-pending-investigations" label="Pending Investigations" value={data?.pending_investigations_patients ?? 0} icon={FlaskConical} accent="text-orange-500" onClick={() => navigate("/doctor/patients?risk_filter=pending_investigations")} />
          </div>
        </div>

        <div className="lg:col-span-1 space-y-6">
          <div className="card-surface card-surface-hover rounded-xl border border-slate-200 bg-white shadow-sm p-6" data-testid="current-patient-card">
            <h2 className="font-semibold text-slate-900">Current Patient</h2>
            {data?.current_patient ? (
              <div className="mt-3 space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-slate-400" />
                  <p className="text-lg font-semibold text-slate-900" data-testid="current-patient-name">{data.current_patient.patient_name}</p>
                </div>
                {(data.current_patient.age != null || data.current_patient.gender) && (
                  <p className="text-slate-500" data-testid="current-patient-age-gender">
                    {[data.current_patient.age != null ? `${data.current_patient.age} Years` : null, data.current_patient.gender].filter(Boolean).join(" • ")}
                  </p>
                )}
                <div className="flex flex-wrap items-center gap-1.5">
                  <TypeBadge type={data.current_patient.appointment_type} />
                  {data.current_patient.ai_risk_level ? (
                    <span
                      data-testid="current-patient-risk-badge"
                      className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-700"
                    >
                      {data.current_patient.ai_risk_emoji} {RISK_LABEL(data.current_patient.ai_risk_level)} Risk
                    </span>
                  ) : (
                    data.current_patient.flags?.map((f) => (
                      <span key={f.key} className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">
                        {f.emoji} {f.label}
                      </span>
                    ))
                  )}
                </div>
                <Button
                  data-testid="open-current-patient-btn"
                  className="mt-3 w-full gap-2 bg-teal-700 hover:bg-teal-800"
                  onClick={() => navigate(`/doctor/patients/${data.current_patient.patient_id}`)}
                >
                  Open Patient <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <p className="mt-3 text-sm text-slate-500" data-testid="no-current-patient">
                No patient is currently with the doctor.
              </p>
            )}
          </div>

          {data?.current_patient && (
            <AiSummaryDigestCard digest={data.current_patient.ai_summary_digest} />
          )}

          {sub && (
            <div className="card-surface card-surface-hover rounded-xl border border-slate-200 bg-white shadow-sm p-6">
              <button data-testid="subscription-card" onClick={() => navigate("/doctor/settings")} className="w-full text-left transition-opacity hover:opacity-80">
                <p className="text-xs uppercase tracking-wide text-slate-400">Subscription</p>
                <p className="mt-1 font-semibold text-slate-900">{sub.plan_label} Plan</p>
                <p className="text-sm text-slate-600">Expires: {formatDate(sub.expiry_date)}</p>
                <p className="text-sm text-slate-600">
                  Appointments: {sub.appointments_used} / {sub.appointments_limit}
                </p>
              </button>
            </div>
          )}
        </div>
      </div>
    </DoctorLayout>
  );
}
